import {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity, Linking, NativeModules} from 'react-native';
import {Text, Appbar, Chip, Divider, IconButton} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {colors, spacing} from '../../theme';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';

const {AudioRecorder} = NativeModules;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const OrderLineInstanceViewScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {instance, line, orderNumber, orderDate} = route.params;

  const [workAssignments, setWorkAssignments] = useState([]);
  const [playingIndex, setPlayingIndex] = useState(null);

  const {fetchByType, getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();
  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (orgUnits.length === 0) fetchOrgUnits();
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (effectiveOrgUnitId) fetchByType('ORDER', effectiveOrgUnitId);
  }, [effectiveOrgUnitId, fetchByType]);

  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';

  useEffect(() => {
    workAssignmentApi.getByInstance(instance.id)
      .then(res => setWorkAssignments(res.data?.data || []))
      .catch(() => {});
  }, [instance.id]);

  const tasks = instance.tasks || [];
  const measurements = instance.measurements || [];
  const optionSelections = (() => {
    const raw = instance.optionSelections;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed.filter(s => s.optionTypeName !== 'Measurement Type');
    } catch (e) { return []; }
  })();
  const unitPrice = Number(line.unitPrice || 0);
  const addOnLines = line.addOnLines || [];
  const addOnsTotal = addOnLines.reduce((sum, ao) => sum + Number(ao.unitPrice || 0), 0);
  const totalPrice = unitPrice + addOnsTotal;
  const audioInstructions = line.audioInstructions || [];
  const videoLink = line.videoLink || '';

  const handlePlayAudio = useCallback(async (url, index) => {
    if (!AudioRecorder) return;
    if (playingIndex === index) {
      try { await AudioRecorder.stopPlayback(); } catch (_) {}
      setPlayingIndex(null);
      return;
    }
    try {
      setPlayingIndex(index);
      await AudioRecorder.playAudio(url);
      setPlayingIndex(null);
    } catch (e) {
      setPlayingIndex(null);
    }
  }, [playingIndex]);

  const handleOpenVideo = useCallback((url) => {
    if (url) Linking.openURL(url).catch(() => {});
  }, []);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={`Order# ${orderNumber}`} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView style={styles.content}>
        {/* Instance Header */}
        <View style={styles.instanceHeader}>
          <Text style={styles.instanceTitle}>{line.catalogItemName} #{instance.instanceCode?.split('-').pop() || '1'}</Text>
          <Text style={styles.instanceCode}>Item # {instance.instanceCode}</Text>
        </View>

        {/* Service Type */}
        {line.serviceCatalogItemName && (
          <View style={styles.section}>
            <View style={styles.serviceTypeRow}>
              <View style={styles.serviceTypeDot} />
              <Text style={styles.serviceTypeText}>{line.serviceCatalogItemName}</Text>
            </View>
          </View>
        )}

        {/* Tasks Table */}
        {tasks.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Tasks</Text>
            <View style={styles.measurementTable}>
              <View style={[styles.tableHeaderRow]}>
                <Text style={[styles.tableCell, {flex: 3}]}>Task Name</Text>
                <Text style={[styles.tableCell, {flex: 2}]}>Assigned To</Text>
                <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>Price</Text>
                <Text style={[styles.tableCell, {flex: 2, textAlign: 'right'}]}>Status</Text>
              </View>
              {tasks.map((task, idx) => (
                <View key={task.id} style={[styles.measurementRow, idx % 2 === 0 && styles.measurementRowAlt]}>
                  <Text style={[styles.tableCell, {flex: 3}]}>{task.taskTypeName}</Text>
                  <Text style={[styles.tableCell, {flex: 2}]}>{task.assignedWorkerName || '—'}</Text>
                  <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>{task.pieceRate != null ? `\u20B9${task.pieceRate}` : '—'}</Text>
                  <Text style={[styles.tableCell, {flex: 2, textAlign: 'right'}]}>{task.status ? t(`order.status.${task.status}`) : '—'}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Work Assignments Table */}
        {workAssignments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Work Assignments</Text>
            <View style={styles.measurementTable}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableCell, {flex: 3}]}>Assignee</Text>
                <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>Price</Text>
                <Text style={[styles.tableCell, {flex: 2, textAlign: 'right'}]}>Status</Text>
              </View>
              {workAssignments.map((a, idx) => (
                <View key={a.id} style={[styles.measurementRow, idx % 2 === 0 && styles.measurementRowAlt]}>
                  <Text style={[styles.tableCell, {flex: 3}]}>{a.workerName || '—'}</Text>
                  <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>{a.pieceRate != null ? `\u20B9${a.pieceRate}` : '—'}</Text>
                  <Text style={[styles.tableCell, {flex: 2, textAlign: 'right'}]}>{a.status || '—'}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <Divider style={styles.divider} />

        {/* Price Break Up */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Price Break up</Text>
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Price</Text>
            <Text style={styles.priceValue}>{'\u20B9'}{unitPrice.toLocaleString()}</Text>
          </View>
          {addOnLines.map(ao => (
            <View key={ao.id} style={styles.priceRow}>
              <Text style={styles.priceLabel}>{ao.itemName}</Text>
              <Text style={styles.priceValue}>{'\u20B9'}{Number(ao.unitPrice || 0).toLocaleString()}</Text>
            </View>
          ))}
          <Divider style={styles.priceDivider} />
          <View style={styles.priceRow}>
            <Text style={styles.priceTotalLabel}>Total Price</Text>
            <Text style={styles.priceTotalValue}>{'\u20B9'}{totalPrice.toLocaleString()}</Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        {/* Dates */}
        <View style={styles.section}>
          {orderDate && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Order Date</Text>
              <Text style={styles.rowValue}>{formatDate(orderDate)}</Text>
            </View>
          )}
          {line.trialDates && line.trialDates.length > 0 && line.trialDates.map((td, idx) => (
            <View key={idx} style={styles.row}>
              <Text style={styles.rowLabel}>Trial Date{line.trialDates.length > 1 ? ` ${idx + 1}` : ''}</Text>
              <Text style={styles.rowValue}>{formatDate(td)}</Text>
            </View>
          ))}
          {line.deliveryDate && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Delivery Date</Text>
              <Text style={styles.rowValue}>{formatDate(line.deliveryDate)}</Text>
            </View>
          )}
          {line.functionDate && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Function Date</Text>
              <Text style={styles.rowValue}>{formatDate(line.functionDate)}</Text>
            </View>
          )}
        </View>

        {/* Audio & Video */}
        {(audioInstructions.length > 0 || !!videoLink) && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.mediaSection}>
              <Text style={styles.sectionTitle}>Audio & Video</Text>
              {audioInstructions.map((audio, idx) => (
                <TouchableOpacity key={idx} style={styles.audioItem} onPress={() => handlePlayAudio(audio.url, idx)} activeOpacity={0.7}>
                  <Icon
                    name={playingIndex === idx ? 'stop-circle' : 'play-circle'}
                    size={30}
                    color={playingIndex === idx ? colors.error : colors.primary}
                  />
                  <View style={styles.audioInfo}>
                    <Text style={styles.audioTitle} numberOfLines={1}>
                      {playingIndex === idx ? 'Playing...' : (audio.note || `Audio ${idx + 1}`)}
                    </Text>
                    {audio.note && playingIndex !== idx ? (
                      <Text style={styles.audioSubtitle}>Audio {idx + 1}</Text>
                    ) : null}
                  </View>
                </TouchableOpacity>
              ))}
              {!!videoLink && (
                <TouchableOpacity style={styles.videoItem} onPress={() => handleOpenVideo(videoLink)} activeOpacity={0.7}>
                  <Icon name="video-outline" size={26} color={colors.primary} />
                  <Text style={styles.videoLink} numberOfLines={1}>
                    {videoLink}
                  </Text>
                  <Icon name="open-in-new" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
          </>
        )}

        <Divider style={styles.divider} />

        {/* Other Details */}
        <View style={styles.section}>
          {line.specialInstructions ? (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Instructions</Text>
              <Text style={[styles.rowValue, {flex: 1, textAlign: 'right'}]}>{line.specialInstructions}</Text>
            </View>
          ) : null}
          {instance.assignedWorkerName ? (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Assigned Worker</Text>
              <Text style={styles.rowValue}>{instance.assignedWorkerName}</Text>
            </View>
          ) : null}
          {line.salesStaffName ? (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Sales Staff</Text>
              <Text style={styles.rowValue}>{line.salesStaffName}</Text>
            </View>
          ) : null}
          {line.urgent && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Urgent</Text>
              <Text style={[styles.rowValue, {color: colors.error}]}>Yes</Text>
            </View>
          )}
        </View>

        {/* Measurements */}
        {measurements.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Measurements ({measurementUnit === 'CMS' ? 'cms' : 'inches'})</Text>
              <View style={styles.measurementTable}>
                {measurements.map((m, idx) => {
                  const imgUrl = m.imageUrl1 || m.imageUrl2;
                  return (
                    <View key={idx} style={[styles.measurementRow, idx % 2 === 0 && styles.measurementRowAlt]}>
                      {imgUrl ? (
                        <Image source={{uri: imgUrl}} style={styles.measurementImage} />
                      ) : (
                        <View style={styles.measurementImageEmpty} />
                      )}
                      <Text style={styles.measurementName}>{m.globalOptionName || m.customOptionName}</Text>
                      <Text style={styles.measurementValue}>{m.value}</Text>
                    </View>
                  );
                })}
              </View>
            </View>
          </>
        )}

        {/* Options */}
        {optionSelections.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Options</Text>
              <View style={styles.measurementTable}>
                {optionSelections.map((sel, idx) => {
                  const imgUrl = sel.renderType !== 'TEXT'
                    ? (sel.selectedOptions || []).find(o => o.imageUrl1)?.imageUrl1
                    : null;
                  return (
                    <View key={idx} style={[styles.measurementRow, idx % 2 === 0 && styles.measurementRowAlt]}>
                      {imgUrl ? (
                        <Image source={{uri: imgUrl}} style={styles.measurementImage} />
                      ) : (
                        <View style={styles.measurementImageEmpty} />
                      )}
                      <Text style={styles.measurementName}>{sel.optionTypeName}</Text>
                      <Text style={styles.optionValue}>
                        {sel.renderType === 'TEXT'
                          ? sel.textValue
                          : (sel.selectedOptions || []).map(o => o.name).join(', ')}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
          </>
        )}

        {/* Cloth Images */}
        {line.clothImageUrls && line.clothImageUrls.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Cloth Image(s)</Text>
              <View style={styles.imageRow}>
                {line.clothImageUrls.map((url, idx) => (
                  <Image key={idx} source={{uri: url}} style={styles.imageThumb} />
                ))}
              </View>
            </View>
          </>
        )}

        {/* Pattern Images */}
        {line.patternImageUrls && line.patternImageUrls.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Pattern Image(s)</Text>
              <View style={styles.imageRow}>
                {line.patternImageUrls.map((url, idx) => (
                  <Image key={idx} source={{uri: url}} style={styles.imageThumb} />
                ))}
              </View>
            </View>
          </>
        )}

        <View style={{height: spacing.xxl}} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {flex: 1},
  instanceHeader: {
    backgroundColor: '#EEEEEE',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  instanceTitle: {fontSize: 20, fontWeight: '700', color: colors.text},
  instanceCode: {fontSize: 14, color: colors.textSecondary, marginTop: 2},
  section: {paddingHorizontal: spacing.md, paddingVertical: spacing.sm},
  sectionTitle: {fontSize: 16, fontWeight: '700', color: colors.text, marginBottom: spacing.sm},
  divider: {marginHorizontal: spacing.md},
  serviceTypeRow: {flexDirection: 'row', alignItems: 'center'},
  serviceTypeDot: {width: 12, height: 12, borderRadius: 6, backgroundColor: colors.textSecondary, marginRight: spacing.sm},
  serviceTypeText: {fontSize: 15, color: colors.text},
  row: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  rowLabel: {fontSize: 14, color: colors.textSecondary},
  rowValue: {fontSize: 14, fontWeight: '500', color: colors.text},
  tableHeaderRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    backgroundColor: '#E8E8E8', borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  tableCell: {fontSize: 13, color: colors.text},
  priceRow: {flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, paddingHorizontal: spacing.sm},
  priceLabel: {fontSize: 14, color: colors.textSecondary},
  priceValue: {fontSize: 14, color: colors.text},
  priceDivider: {marginVertical: 4, marginHorizontal: spacing.sm},
  priceTotalLabel: {fontSize: 14, fontWeight: '700', color: colors.text},
  priceTotalValue: {fontSize: 14, fontWeight: '700', color: colors.text},
  measurementTable: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 4, overflow: 'hidden',
  },
  measurementRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm, paddingHorizontal: spacing,
    borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  measurementRowAlt: {backgroundColor: '#FAFAFA'},
  measurementImage: {
    width: 50, height: 50, borderRadius: 4, borderWidth: 0, borderColor: '#CCC',
    backgroundColor: colors.background, marginRight: spacing.sm,
  },
  measurementImageEmpty: {
    width: 50, height: 50, borderRadius: 4, borderWidth: 1, borderColor: '#CCC',
    backgroundColor: colors.background, marginRight: spacing.sm,
  },
  measurementName: {flex: 1, fontSize: 14, color: colors.text},
  measurementValue: {width: 60, textAlign: 'center', fontSize: 14, fontWeight: '600', color: colors.text},
  optionValue: {flex: 1, textAlign: 'right', fontSize: 14, fontWeight: '600', color: colors.text},
  imageRow: {flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap'},
  imageThumb: {width: 120, height: 120, borderRadius: 8, backgroundColor: colors.border},
  mediaSection: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: '#F5F0FF',
    marginHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D9CCF0',
    marginVertical: spacing.xs,
  },
  audioItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
  },
  audioInfo: {flex: 1, marginLeft: spacing.sm},
  audioTitle: {fontSize: 14, fontWeight: '500', color: colors.text},
  audioSubtitle: {fontSize: 11, color: colors.textSecondary, marginTop: 1},
  videoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.sm,
  },
  videoLink: {flex: 1, fontSize: 13, color: colors.primary, textDecorationLine: 'underline'},
});

export default OrderLineInstanceViewScreen;
